/**
 * Shared Tool Wizard framework used by VSM / 5S / Kaizen.
 * Handles: multi-step navigation, save-on-next, progress dot UI.
 */
import { useState } from "react";
import { ChevronLeft, ChevronRight, Check, Save } from "lucide-react";

export default function ToolWizard({
  project,
  steps,             // [{ key, label, render: (props)=>JSX, validate?: (data)=>string|null }]
  onSave,            // async (patch:{tool_data, wizard_progress}) => void
  completedLabel = "Complete",
}) {
  const [current, setCurrent] = useState(project?.wizard_progress || 1);
  const [data, setData] = useState(project?.tool_data || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const stepIndex = current - 1;
  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  const updateData = (patch) => setData((d) => ({ ...d, ...patch }));

  const save = async (nextStep) => {
    setError("");
    setSaving(true);
    try {
      const v = step.validate ? step.validate(data) : null;
      if (v) { setError(v); setSaving(false); return; }
      await onSave({ tool_data: data, wizard_progress: Math.max(project?.wizard_progress || 1, nextStep || current) });
      if (nextStep) setCurrent(nextStep);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-center gap-2 mb-5 flex-wrap" data-testid="tool-wizard-steps">
        {steps.map((s, i) => {
          const n = i + 1;
          const done = n < current;
          const active = n === current;
          return (
            <button
              key={s.key}
              data-testid={`step-${s.key}`}
              onClick={() => n <= (project?.wizard_progress || 1) && setCurrent(n)}
              disabled={n > (project?.wizard_progress || 1)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${active ? "bg-sage-600 text-white" : done ? "bg-sage-100 text-sage-700 hover:bg-sage-200" : "bg-sage-50 text-sage-400 cursor-not-allowed"}`}
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center ${done ? "bg-sage-600 text-white" : active ? "bg-white/20" : ""}`}>
                {done ? <Check className="w-3 h-3" /> : n}
              </span>
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-[#E2E8E3] rounded-md p-6" data-testid={`step-content-${step.key}`}>
        <h3 className="font-heading text-lg font-semibold text-sage-900 mb-1">{step.label}</h3>
        <div className="mt-4">{step.render({ data, updateData })}</div>
      </div>

      {error && <div className="mt-3 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700">{error}</div>}

      <div className="flex items-center justify-between mt-5">
        <button
          data-testid="wizard-prev-button"
          onClick={() => setCurrent((c) => Math.max(1, c - 1))}
          disabled={current === 1}
          className="flex items-center gap-1 border border-sage-200 rounded-md px-3 py-2 text-sm text-sage-700 hover:bg-sage-50 disabled:opacity-30"
        >
          <ChevronLeft className="w-4 h-4" /> Previous
        </button>
        <button
          data-testid="wizard-save-button"
          onClick={() => save(null)}
          disabled={saving}
          className="flex items-center gap-1 border border-sage-200 rounded-md px-3 py-2 text-sm text-sage-700 hover:bg-sage-50"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save"}
        </button>
        {!isLast ? (
          <button
            data-testid="wizard-next-button"
            onClick={() => save(current + 1)}
            disabled={saving}
            className="flex items-center gap-1 bg-sage-600 text-white rounded-md px-3 py-2 text-sm font-semibold hover:bg-sage-700"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            data-testid="wizard-complete-button"
            onClick={() => save(steps.length)}
            disabled={saving}
            className="flex items-center gap-1 bg-sage-600 text-white rounded-md px-3 py-2 text-sm font-semibold hover:bg-sage-700"
          >
            <Check className="w-4 h-4" /> {completedLabel}
          </button>
        )}
      </div>
    </div>
  );
}
